import React, { useCallback, useEffect, useState } from "react";
import { ApartmentStatTrend, Convert, OwnerAirbnbReview } from "@/models/responses";
import { ColDef, GridApi } from "ag-grid-community";
import { euroFormatterTwoDecimals, occupancyBarRenderer, paramsEuroFormatter, percentageFormatterMultipliedByHundred } from "@/utils/formatters";
import Grid, { GridDropdownFilter } from "../lib/organisms/Grid";
import { PeriodName } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { PeriodDates, usePeriodDates } from "@/hooks/usePeriodDates";
import { formatDateToISODate } from "@/utils/dates";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

interface TrendsRow {
    apartment: string;
    numberOfVisits: number;
    grossIncome: number;
    grossIncomePreviousPeriod: number;
    grossIncomePreviousYear: number;
    netIncome: number;
    netIncomePreviousPeriod: number;
    netIncomePreviousYear: number;
    netAdr: number;
    netAdrPreviousPeriod: number;
    netAdrPreviousYear: number;
    grossAdr: number;
    grossAdrPreviousPeriod: number;
    grossAdrPreviousYear: number;
    occupancy: number;
    occupancyPreviousPeriod: number;
    occupancyPreviousYear: number;
    daysBooked: number;
    daysBookedPreviousPeriod: number;
    daysBookedPreviousYear: number;
    daysAvailable: number;
    daysAvailablePreviousPeriod: number;
    daysAvailablePreviousYear: number;
    payout: number;
    payoutPreviousYear: number;
    payoutRatio: number;
    payoutRatioPreviousYear: number;
    reviewScore: number | null;
    reviewScorePreviousPeriod: number | null;
    reviewScorePreviousYear: number | null;
}

interface RawTrendsData {
    trends: ApartmentStatTrend[];
    reviewsCurrentPeriod: OwnerAirbnbReview[];
    reviewsPreviousPeriod: OwnerAirbnbReview[];
    reviewsPreviousYear: OwnerAirbnbReview[];
}

const UserTrends: React.FC = () => {
    const { selectedPeriod, selectedYear, periodOptions, yearOptions, selectedOwner, ownerOptions } = useAppData();
    const periodDates = usePeriodDates(selectedYear, selectedPeriod);

    const apartmentEntities = useActiveApartmentEntities();

    const { setSelectedPeriod, setSelectedYear, setSelectedOwner } = useUserSettingsDispatch();
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    // Raw fetched data — held in state so the apartment-filter predicates and
    // apartment-entity lookup (both of which can change identity after the
    // fetch) can paint into the grid via a second effect without re-firing the
    // network calls.
    const [rawTrendsData, setRawTrendsData] = useState<RawTrendsData | null>(null);

    // Shared apartment filters: predicates applied to each trend row's apartment
    // before it reaches the grid, leaving the Period/Year/Owner controls untouched
    // (FR-004, apartment-filters-everywhere).
    const { predicates } = useApartmentFilters();

    const mapTrendToRow = useCallback(
        (
            trend: ApartmentStatTrend,
            reviewsCurrentPeriod: OwnerAirbnbReview | undefined,
            reviewsPreviousPeriod: OwnerAirbnbReview | undefined,
            reviewsPreviousYear: OwnerAirbnbReview | undefined,
        ) => {
            return {
                apartment: apartmentEntities[trend.apartmentId]?.name || "Unknown",
                numberOfVisits: trend.numberOfVisits,
                grossIncome: trend.grossIncome,
                grossIncomePreviousPeriod: trend.grossIncomePreviousPeriod,
                grossIncomePreviousYear: trend.grossIncomePreviousYear,
                netIncome: trend.netIncome,
                netIncomePreviousPeriod: trend.netIncomePreviousPeriod,
                netIncomePreviousYear: trend.netIncomePreviousYear,
                netAdr: trend.netAdr,
                netAdrPreviousPeriod: trend.netAdrPreviousPeriod,
                netAdrPreviousYear: trend.netAdrPreviousYear,
                grossAdr: trend.grossAdr,
                grossAdrPreviousPeriod: trend.grossAdrPreviousPeriod,
                grossAdrPreviousYear: trend.grossAdrPreviousYear,
                occupancy: trend.occupancy,
                occupancyPreviousPeriod: trend.occupancyPreviousPeriod,
                occupancyPreviousYear: trend.occupancyPreviousYear,
                daysBooked: trend.daysBooked,
                daysBookedPreviousPeriod: trend.daysBookedPreviousPeriod,
                daysBookedPreviousYear: trend.daysBookedPreviousYear,
                daysAvailable: trend.daysAvailable,
                daysAvailablePreviousPeriod: trend.daysAvailablePreviousPeriod,
                daysAvailablePreviousYear: trend.daysAvailablePreviousYear,
                payout: trend.payout,
                payoutPreviousYear: trend.payoutPreviousYear,
                payoutRatio: trend.payoutRatio,
                payoutRatioPreviousYear: trend.payoutRatioPreviousYear,
                reviewScore: reviewsCurrentPeriod?.overallScore ?? null,
                reviewScorePreviousPeriod: reviewsPreviousPeriod?.overallScore ?? null,
                reviewScorePreviousYear: reviewsPreviousYear?.overallScore ?? null,
            };
        },
        [apartmentEntities],
    );

    const getReviewsForPeriod = useCallback(async (periodEndDate: Date, ownerId: string) => {
        const firstDayOfNextMonth = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1);
        const reviewsUrl = `${apiUrl}${getApiBasePath()}/owners/${ownerId}/airbnb-reviews?date=${formatDateToISODate(firstDayOfNextMonth)}`;
        const response = await fetch(reviewsUrl, fetchOptions);
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
        const data = await response.text();
        return Convert.toGetOwnerAirbnbReviewsResponse(data).reviews;
    }, []);

    // Fetch the raw trend + review payloads. Deliberately depends only on the
    // upstream period / owner inputs (and the stable `getReviewsForPeriod`
    // callback) so it doesn't refire when downstream concerns like
    // `apartmentEntities` or `predicates` change. That separation prevents
    // multiple concurrent fetch chains kicking off as the page hydrates.
    //
    // The three review fetches are issued in parallel — they're independent
    // (each maps to a distinct date), so awaiting them concurrently halves
    // the page-load latency and avoids interleaving network gaps.
    const getAndSetTrends = useCallback(
        async (periodDates: PeriodDates, year: string, period: string, ownerId: string) => {
            const [reviewsCurrentPeriod, reviewsPreviousPeriod, reviewsPreviousYear] = await Promise.all([
                getReviewsForPeriod(periodDates.current.endDate, ownerId),
                getReviewsForPeriod(periodDates.previousPeriod.endDate, ownerId),
                getReviewsForPeriod(periodDates.previousYear.endDate, ownerId),
            ]);
            const trendsUrl = `${apiUrl}${getApiBasePath()}/owners/${ownerId}/trends?year=${year}&period=${period}`;
            const response = await fetch(trendsUrl, fetchOptions);
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.text();
            const trends = Convert.toGetApartmentStatTrends(data).apartmentStatTrends;
            setRawTrendsData({
                trends,
                reviewsCurrentPeriod,
                reviewsPreviousPeriod,
                reviewsPreviousYear,
            });
        },
        [getReviewsForPeriod],
    );

    // Paint the grid whenever raw data, apartment entities, predicates, or the
    // grid api change. Runs in O(n) once data is available.
    useEffect(() => {
        if (!gridApi || !rawTrendsData) {
            return;
        }
        const rows = rawTrendsData.trends
            .filter((trend) => {
                // Drop any trend row whose apartment fails one of the shared
                // apartment-level predicates. A trend for an unknown
                // apartment (entities map miss) is excluded — no way to
                // evaluate predicates against `undefined`, and surfacing an
                // orphan row would be wrong.
                const apartment = apartmentEntities[trend.apartmentId];
                if (!apartment) {
                    return false;
                }
                return predicates.every((predicate) => predicate(apartment));
            })
            .map((trend) => {
                const reviewsForApartmentCurrentPeriod = rawTrendsData.reviewsCurrentPeriod.find(
                    (review) => review.apartmentId === trend.apartmentId,
                );
                const reviewsForApartmentPreviousPeriod = rawTrendsData.reviewsPreviousPeriod.find(
                    (review) => review.apartmentId === trend.apartmentId,
                );
                const reviewsForApartmentPreviousYear = rawTrendsData.reviewsPreviousYear.find(
                    (review) => review.apartmentId === trend.apartmentId,
                );
                return mapTrendToRow(trend, reviewsForApartmentCurrentPeriod, reviewsForApartmentPreviousPeriod, reviewsForApartmentPreviousYear);
            })
            .sort((a, b) => a.apartment.localeCompare(b.apartment));
        gridApi.setGridOption("rowData", rows);
    }, [gridApi, rawTrendsData, apartmentEntities, predicates, mapTrendToRow]);

    const daysBookedColumn: ColDef<TrendsRow> = {
        field: "daysBooked",
        headerName: "Days Booked",
        width: 100,
        filter: "agNumberColumnFilter",
    };
    const grossIncomeColumn: ColDef<TrendsRow> = {
        field: "grossIncome",
        headerName: "Gross Income",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.grossIncome);
            }
        },
    };
    const netIncomeColumn: ColDef<TrendsRow> = {
        field: "netIncome",
        headerName: "Net Income",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.netIncome);
            }
        },
    };
    const numberOfVisitsColumn: ColDef<TrendsRow> = {
        field: "numberOfVisits",
        headerName: "Number of Visits",
        width: 130,
        filter: "agNumberColumnFilter",
        cellDataType: "number",
    };
    const netAdrColumn: ColDef<TrendsRow> = {
        field: "netAdr",
        headerName: "Net Adr",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 95,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.netAdr);
            }
        },
    };
    const grossAdrColumn: ColDef<TrendsRow> = {
        field: "grossAdr",
        headerName: "Gross Adr",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 95,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.grossAdr);
            }
        },
    };
    let reviewScoreCurrent: ColDef<TrendsRow, number | null> = {
        field: "reviewScore",
        headerName: "Airbnb Review Rating",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    let reviewScorePreviousPeriod: ColDef<TrendsRow, number | null> = {
        field: "reviewScorePreviousPeriod",
        headerName: "Airbnb Review Rating Previous Period",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    let reviewScorePreviousYear: ColDef<TrendsRow, number | null> = {
        field: "reviewScorePreviousYear",
        headerName: "Airbnb Review Rating Previous Year",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    const columnDefinitions: ColDef<TrendsRow>[] = [
        {
            field: "apartment",
            headerName: "Apartment",
            width: 280,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        numberOfVisitsColumn,
        {
            field: "occupancy",
            headerName: "Occupancy",
            cellRenderer: occupancyBarRenderer,
            cellClass: "booleanType",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.occupancy);
                }
            },
            width: 140,
        },
        {
            field: "occupancyPreviousPeriod",
            headerName: "Occupancy Previous Period",
            cellRenderer: occupancyBarRenderer,
            cellClass: "booleanType",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.occupancyPreviousPeriod);
                }
            },
            width: 140,
        },
        {
            field: "occupancyPreviousYear",
            headerName: "Occupancy Previous Year",
            cellRenderer: occupancyBarRenderer,
            cellClass: "booleanType",
            width: 140,
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.occupancyPreviousYear);
                }
            },
        },
        grossIncomeColumn,
        {
            field: "grossIncomePreviousPeriod",
            headerName: "Gross Income Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossIncomePreviousPeriod);
                }
            },
        },
        {
            field: "grossIncomePreviousYear",
            headerName: "Gross Income Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossIncomePreviousYear);
                }
            },
        },
        daysBookedColumn,
        {
            field: "daysBookedPreviousPeriod",
            headerName: "Days Booked Previous Period",
            width: 150,
            filter: "agNumberColumnFilter",
            cellDataType: "number",
        },
        {
            field: "daysBookedPreviousYear",
            headerName: "Days Booked Previous Year",
            width: 150,
            filter: "agNumberColumnFilter",
            cellDataType: "number",
        },
        {
            field: "daysAvailable",
            headerName: "Days Available",
            width: 150,
            filter: "agNumberColumnFilter",
            cellDataType: "number",
        },
        {
            field: "daysAvailablePreviousPeriod",
            headerName: "Days Available Previous Period",
            width: 150,
            filter: "agNumberColumnFilter",
            cellDataType: "number",
        },
        {
            field: "daysAvailablePreviousYear",
            headerName: "Days Available Previous Year",
            width: 150,
            filter: "agNumberColumnFilter",
            cellDataType: "number",
        },

        netIncomeColumn,
        {
            field: "netIncomePreviousPeriod",
            headerName: "Net Income Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netIncomePreviousPeriod);
                }
            },
        },
        {
            field: "netIncomePreviousYear",
            headerName: "Net Income Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netIncomePreviousYear);
                }
            },
        },
        netAdrColumn,
        {
            field: "netAdrPreviousPeriod",
            headerName: "Net Adr Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netAdrPreviousPeriod);
                }
            },
        },
        {
            field: "netAdrPreviousYear",
            headerName: "Net Adr Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netAdrPreviousYear);
                }
            },
        },
        grossAdrColumn,
        {
            field: "grossAdrPreviousPeriod",
            headerName: "Gross Adr Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossAdrPreviousPeriod);
                }
            },
        },
        {
            field: "grossAdrPreviousYear",
            headerName: "Gross Adr Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 150,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossAdrPreviousYear);
                }
            },
        },
        {
            field: "payout",
            headerName: "Payout",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payout);
                }
            },
        },
        {
            field: "payoutPreviousYear",
            headerName: "Payout Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutPreviousYear);
                }
            },
        },
        {
            field: "payoutRatio",
            headerName: "Payout Ratio",
            valueFormatter: percentageFormatterMultipliedByHundred,
            cellDataType: "number",
            cellClass: "booleanType",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutRatio * 100);
                }
            },
        },
        {
            field: "payoutRatioPreviousYear",
            headerName: "Payout Ratio Previous Year",
            valueFormatter: percentageFormatterMultipliedByHundred,
            cellDataType: "number",
            cellClass: "booleanType",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutRatioPreviousYear * 100);
                }
            },
        },
        reviewScoreCurrent,
        reviewScorePreviousPeriod,
        reviewScorePreviousYear,
    ];

    useEffect(() => {
        // `getAndSetTrends` is async and calls `setRawTrendsData` after its
        // network roundtrip completes — that's not a synchronous setState in
        // the effect body but the linter can't see through the await chain.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void getAndSetTrends(periodDates, selectedYear.toString(), selectedPeriod, selectedOwner.toString());
    }, [periodDates, selectedPeriod, selectedYear, selectedOwner, getAndSetTrends]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Owner",
            options: ownerOptions,
            selectedOption: selectedOwner.toString(),
            onSelectionChanged: (apartment) => setSelectedOwner(Number(apartment)),
        },
    ];

    return (
        <div className="flex flex-col space-y-2" data-testid="user-trends-page">
            <ApartmentFilterBar />
            <Grid<TrendsRow>
                columnDefs={columnDefinitions}
                dropdownFilters={gridDropdownFilters}
                gridName={"Trends"}
                initialRowData={[]}
                getRowId={(params) => params.data.apartment}
                onGridReady={setGridApi}
                columnsToAggregate={[
                    { colDef: daysBookedColumn, formatter: (x) => x },
                    { colDef: numberOfVisitsColumn, formatter: (x) => x },
                    { colDef: grossIncomeColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: netIncomeColumn, formatter: euroFormatterTwoDecimals },
                    {
                        colDef: netAdrColumn,
                        formatter: euroFormatterTwoDecimals,
                        aggregateFunction: "avg",
                    },
                    {
                        colDef: grossAdrColumn,
                        formatter: euroFormatterTwoDecimals,
                        aggregateFunction: "avg",
                    },
                ]}
            />
        </div>
    );
};
export default UserTrends;
